import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	ChevronLeft,
	ChevronRight,
	Download,
	Maximize2,
	Minus,
	Plus,
	RotateCcw,
} from 'lucide-react'
import { type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { fetchInlineUrls } from 'slices/documentLibrarySlice'
import type {
	DocumentLibraryState,
	LibraryDocument,
} from '../types/documentLibrary.types'
import { downloadAndSave } from '../services/libraryDocumentService'
import { getCurrentVersion } from '../types/documentLibrary.types'
import { getDocumentIcon } from '../utils/getDocumentIcon'
import DocumentPreviewPane from './DocumentPreviewPane'
import DocumentPreviewThumbnails from './DocumentPreviewThumbnails'

interface DocumentPreviewDialogProps {
	open: boolean
	onClose: () => void
	documentId: string | null
}

// Refresh inline URLs once they're more than this old. Backend signs
// for 600s; we rebuild at 480s to leave headroom.
const INLINE_URL_TTL_MS = 8 * 60 * 1000

const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
	open,
	onClose,
	documentId,
}) => {
	const dispatch = useDispatch<any>()
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)
	const document = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId ? state.libraryDocuments.entities[documentId] : undefined,
	)
	const cached = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId ? state.libraryDocuments.inlineUrlsByDocument[documentId] : undefined,
	)
	const inlineUrls = cached?.response ?? null

	const [pageNumber, setPageNumber] = useState(1)
	const [numPages, setNumPages] = useState(0)
	const [downloading, setDownloading] = useState(false)
	const transformRef = useRef<ReactZoomPanPinchRef | null>(null)

	// Reset transient state when document changes / dialog reopens.
	useEffect(() => {
		setPageNumber(1)
		setNumPages(0)
	}, [documentId, open])

	// Lazy-fetch inline URLs on open / cache miss / staleness.
	useEffect(() => {
		if (!open || !documentId) return
		const expired =
			cached && Date.now() - cached.fetchedAt > INLINE_URL_TTL_MS
		if (!cached || expired) {
			dispatch(fetchInlineUrls({ id: documentId }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, documentId])

	// Keyboard navigation: ←/→ paginate, +/-/0 zoom.
	useEffect(() => {
		if (!open) return
		const handler = (e: KeyboardEvent) => {
			const family = inlineUrls?.family
			const supportsPaging = family === 'pdf' || family === 'office'
			if (e.key === 'ArrowLeft' && supportsPaging) {
				e.preventDefault()
				setPageNumber(p => Math.max(1, p - 1))
			} else if (e.key === 'ArrowRight' && supportsPaging) {
				e.preventDefault()
				setPageNumber(p => Math.min(numPages || 1, p + 1))
			} else if (e.key === '+' || e.key === '=') {
				transformRef.current?.zoomIn()
			} else if (e.key === '-' || e.key === '_') {
				transformRef.current?.zoomOut()
			} else if (e.key === '0') {
				transformRef.current?.resetTransform()
			}
		}
		window.addEventListener('keydown', handler)
		return () => window.removeEventListener('keydown', handler)
	}, [open, numPages, inlineUrls?.family])

	const currentVersion = getCurrentVersion(document)
	const filename = useMemo(
		() =>
			inlineUrls?.originalFilename ||
			currentVersion?.originalFilename ||
			document?.libraryName ||
			'',
		[inlineUrls, currentVersion, document],
	)

	const handleDownload = useCallback(async () => {
		if (!documentId || downloading) return
		setDownloading(true)
		try {
			await downloadAndSave(
				{
					id: documentId,
					versionId: currentVersion?._id,
					filename: filename || 'download',
				},
				{
					headers: {
						Authorization: `Bearer ${userInfo?.token || ''}`,
					},
				},
			)
		} finally {
			setDownloading(false)
		}
	}, [documentId, currentVersion?._id, filename, downloading, userInfo?.token])

	const handleLoadSuccess = useCallback(
		({ numPages: total }: { numPages: number }) => {
			setNumPages(total)
		},
		[],
	)

	const supportsPaging =
		inlineUrls?.family === 'pdf' || inlineUrls?.family === 'office'
	const supportsZoom =
		inlineUrls?.family === 'pdf' ||
		inlineUrls?.family === 'office' ||
		inlineUrls?.family === 'image'

	const fileIcon = getDocumentIcon(filename)

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-[1200px] w-[90vw]">
				<DialogHeader>
					<div className="flex items-center justify-between gap-3 pr-2">
						<div className="flex items-center gap-3 min-w-0">
							<div
								className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-[11px] flex-shrink-0"
								style={{ backgroundColor: fileIcon.color }}
							>
								{fileIcon.label}
							</div>
							<div className="min-w-0">
								<DialogTitle className="truncate" title={filename}>
									{filename || 'Loading…'}
								</DialogTitle>
								<DialogDescription>
									{document?.libraryName &&
									document.libraryName !== filename ? (
										<>Library document — {document.libraryName}</>
									) : (
										<>Use ← / → to page, +/− to zoom, 0 to reset.</>
									)}
								</DialogDescription>
							</div>
						</div>
						<Button
							type="button"
							variant="ghost"
							onClick={handleDownload}
							disabled={downloading}
							className="gap-1 whitespace-nowrap"
						>
							<Download className="w-4 h-4" />
							{downloading ? 'Downloading…' : 'Download'}
						</Button>
					</div>
				</DialogHeader>

				<div className="flex" style={{ height: '70vh' }}>
					{supportsPaging && inlineUrls?.previewUrl && numPages > 1 && (
						<div className="w-[180px] flex-shrink-0">
							<DocumentPreviewThumbnails
								url={inlineUrls.previewUrl}
								numPages={numPages}
								currentPage={pageNumber}
								onSelectPage={setPageNumber}
							/>
						</div>
					)}
					<div className="flex-1 min-w-0">
						<DocumentPreviewPane
							inlineUrls={inlineUrls}
							pageNumber={pageNumber}
							onLoadSuccess={handleLoadSuccess}
							onDownload={handleDownload}
							transformRef={transformRef}
						/>
					</div>
				</div>

				<div className="flex items-center justify-between px-5 py-3 border-t border-gray-200">
					{supportsPaging ? (
						<div className="flex items-center gap-2">
							<Button
								type="button"
								variant="ghost"
								onClick={() => setPageNumber(p => Math.max(1, p - 1))}
								disabled={pageNumber <= 1}
								className="gap-1"
								aria-label="Previous page"
							>
								<ChevronLeft className="w-4 h-4" />
								Prev
							</Button>
							<span className="text-xs text-gray-600 min-w-[80px] text-center">
								Page {pageNumber} of {numPages || '…'}
							</span>
							<Button
								type="button"
								variant="ghost"
								onClick={() =>
									setPageNumber(p => Math.min(numPages || 1, p + 1))
								}
								disabled={!numPages || pageNumber >= numPages}
								className="gap-1"
								aria-label="Next page"
							>
								Next
								<ChevronRight className="w-4 h-4" />
							</Button>
						</div>
					) : (
						<div />
					)}
					{supportsZoom && (
						<div className="flex items-center gap-1">
							<Button
								type="button"
								variant="ghost"
								onClick={() => transformRef.current?.zoomOut()}
								className="gap-1"
								aria-label="Zoom out"
							>
								<Minus className="w-4 h-4" />
							</Button>
							<Button
								type="button"
								variant="ghost"
								onClick={() => transformRef.current?.resetTransform()}
								className="gap-1"
								aria-label="Reset zoom"
							>
								<RotateCcw className="w-4 h-4" />
							</Button>
							<Button
								type="button"
								variant="ghost"
								onClick={() => transformRef.current?.zoomIn()}
								className="gap-1"
								aria-label="Zoom in"
							>
								<Plus className="w-4 h-4" />
							</Button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default DocumentPreviewDialog
